// A producer's profile (/@handle): two-column layout with identity header,
// tabs of seeds/remixes/likes, and a sidebar with level/badges/stats.

import { useParams, useSearchParams, Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Headphones, Radio, Share, Sprout } from "lucide-react";
import type { Profile, ProfileTab } from "@shared/api-types.js";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { Button } from "@web-shared/components/ui/button";
import { ApiError } from "@web-shared/lib/api";
import { webApi } from "@web-shared/lib/web-api";
import { relativeTime } from "@web-shared/lib/format";
import { cn } from "@web-shared/lib/utils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { CardList, EmptyState } from "../components/CardList";
import { EditProfileButton } from "../components/EditProfile";
import { BadgeShelf, LevelAvatar, LevelBar } from "../components/Level";
import { FollowButton } from "../components/FollowButton";
import { shareProfile } from "../components/share";
import { jamPath, useLiveProducers } from "../live";
import { AmbientGlow } from "@/components/AmbientGlow";

const TABS: Array<{ id: ProfileTab; label: string }> = [
  { id: "seeds", label: "Seeds" },
  { id: "remixes", label: "Remixes" },
  { id: "likes", label: "Likes" },
];

/** `/@handle` — the route is `/:at`, so anything not starting with @ is not a profile. */
export function handleFromParam(param: string | undefined): string | null {
  if (!param || !param.startsWith("@") || param.length < 2) return null;
  return param.slice(1);
}

export function ProfilePage() {
  const { at } = useParams();
  const handle = handleFromParam(at) ?? "";
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === params.get("tab"))?.id ?? "seeds") as ProfileTab;
  const live = useLiveProducers();

  const profile = useQuery({
    queryKey: ["web", "profile", handle.toLowerCase()],
    queryFn: () => webApi<Profile>(`/users/${encodeURIComponent(handle)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  useDocumentTitle(profile.data ? `${profile.data.displayName || profile.data.username} (@${profile.data.handle})` : "");

  if (profile.isPending) return <Skeleton className="h-40 w-full rounded-2xl" />;
  if (profile.isError) {
    const missing = profile.error instanceof ApiError && profile.error.status === 404;
    return <EmptyState>{missing ? `There's no producer called @${handle}.` : "Could not load this profile."}</EmptyState>;
  }
  const p = profile.data;

  return (
    <div className="space-y-0">
      <AmbientGlow />

      {/* ---- Mobile: stacked identity ---- */}
      <header className="sm:hidden pt-3 pb-4 flex flex-col items-center text-center">
        <LevelAvatar profile={p} live={live.has(p.id)} />
        <h1 className="mt-2 text-xl font-black tracking-tight">{p.displayName || p.username}</h1>
        <div className="text-xs text-muted-foreground">@{p.handle}</div>
        {p.bio && <p className="mt-1.5 text-xs text-foreground/65 max-w-[280px]">{p.bio}</p>}
        {live.has(p.id) && (
          <Link
            to={jamPath(live.get(p.id)!.id)}
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-xs text-primary hover:bg-primary/25"
          >
            <Radio className="size-3.5 animate-pulse" /> Jamming now
          </Link>
        )}
        {/* Stats + actions in one row */}
        <div className="mt-3 flex items-center gap-4">
          <div className="flex items-center gap-2.5 text-[11px] text-muted-foreground/60">
            <span className="flex items-center gap-0.5" title={`${p.counts.seeds} seeds`}>
              <Sprout className="size-3" />
              <span className="font-bold text-foreground/80">{p.counts.seeds}</span>
            </span>
            <span className="flex items-center gap-0.5" title={`${p.counts.playsReceived} plays`}>
              <Headphones className="size-3" />
              <span className="font-bold text-foreground/80">{p.counts.playsReceived}</span>
            </span>
            <span className="flex items-center gap-0.5" title={`${p.counts.timesRemixed} times flipped`}>
              <svg className="size-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" /></svg>
              <span className={cn("font-bold", p.counts.timesRemixed === 0 ? "text-muted-foreground/40" : "text-foreground/80")}>{p.counts.timesRemixed}</span>
            </span>
          </div>
          <div className="w-px h-4 bg-strong" />
          <div className="flex items-center gap-1.5">
            {p.isMe ? (
              <EditProfileButton profile={p} iconOnly />
            ) : (
              <FollowButton handle={p.handle} name={p.displayName || p.username} following={p.followedByMe} iconOnly />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg border border-strong"
              title="Share profile"
              aria-label="Share profile"
              onClick={() => void shareProfile({ handle: p.handle, name: p.displayName || p.username })}
            >
              <Share className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* ---- Desktop: side-by-side identity ---- */}
      <header className="hidden sm:flex pt-8 pb-8 flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-7">
          <LevelAvatar profile={p} live={live.has(p.id)} />
          <div className="min-w-0">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">{p.displayName || p.username}</h1>
            <div className="mt-2 text-muted-foreground text-sm">
              @{p.handle} · joined {relativeTime(p.createdAt)}
            </div>
            {p.bio && <p className="mt-3 text-foreground/80 max-w-[520px] text-sm whitespace-pre-line">{p.bio}</p>}
            {live.has(p.id) && (
              <Link
                to={jamPath(live.get(p.id)!.id)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-xs text-primary hover:bg-primary/25"
              >
                <Radio className="size-3.5 animate-pulse" /> Jamming now — listen in
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="size-11 rounded-full border border-strong"
            title="Share profile"
            aria-label="Share profile"
            onClick={() => void shareProfile({ handle: p.handle, name: p.displayName || p.username })}
          >
            <Share className="size-5" />
          </Button>
          {p.isMe ? (
            <EditProfileButton profile={p} />
          ) : (
            <FollowButton handle={p.handle} name={p.displayName || p.username} following={p.followedByMe} />
          )}
        </div>
      </header>

      {/* ---- Below lg: badges (horizontal scroll) — the sidebar that
           otherwise carries them starts at lg. ---- */}
      <section className="lg:hidden mt-2">
        <div className="-mx-4 px-4 overflow-x-auto scrollbar-none">
          <div className="flex justify-center gap-2 w-full min-w-max">
            <BadgeShelf profile={p} />
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="flex items-center border-b border-strong mt-5 sm:mt-0">
        <div className="flex gap-6 sm:gap-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setParams(t.id === "seeds" ? {} : { tab: t.id }, { replace: true })}
              className={cn(
                "pb-2 sm:pb-3 border-b-2 font-semibold text-sm sm:text-base text-muted-foreground transition-colors",
                tab === t.id ? "text-white border-primary" : "border-transparent hover:text-muted-foreground",
              )}
            >
              {t.label}
              {t.id !== "likes" && (
                <span className="ml-1 text-muted-foreground font-normal">
                  {t.id === "seeds" ? p.counts.seeds : p.counts.remixes}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ---- Two-column body (sidebar hidden on mobile) ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-8 pt-4 pb-24">
        <main className="flex flex-col gap-8 min-w-0">
          <section>
            <CardList
              queryKey={["profile", p.id, tab]}
              path={`/users/${encodeURIComponent(p.handle)}/${tab}`}
              empty={
                <div className="rounded-2xl border border-dashed border-strong p-10 text-center">
                  <div className="text-lg font-semibold">
                    {tab === "seeds" ? "No seeds yet" : tab === "remixes" ? "No remixes yet" : "No likes yet"}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {tab === "seeds"
                      ? p.isMe
                        ? "Plant a seed and it shows up here."
                        : `When ${p.displayName || p.username} plants a seed, it shows up here.`
                      : tab === "remixes"
                        ? `When ${p.displayName || p.username} flips someone's seed, it shows up here next to the original.`
                        : `Seeds ${p.displayName || p.username} has liked.`}
                  </p>
                </div>
              }
            />
          </section>
        </main>

        {/* Sidebar — desktop only */}
        <aside className="hidden lg:flex flex-col gap-5">
          <section className="rounded-2xl border border-hairline bg-raised p-5">
            <LevelBar profile={p} />
            <div className="mt-5 border-t border-strong pt-5">
              <BadgeShelf profile={p} />
            </div>
          </section>

          <section className="rounded-2xl border border-hairline bg-raised p-5">
            <div className="font-semibold">Stats</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Seeds</span>
                <span className="font-mono text-foreground/80">{p.counts.seeds}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Plays</span>
                <span className="font-mono text-foreground/80">{p.counts.playsReceived}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Followers</span>
                <span className="font-mono text-foreground/80">{p.counts.followers}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Following</span>
                <span className="font-mono text-foreground/80">{p.counts.following}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Likes received</span>
                <span className="font-mono text-foreground/80">{p.counts.likesReceived}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span title="Takes by other producers that grew from their seeds">Times flipped</span>
                <span className="font-mono text-foreground/80">{p.counts.timesRemixed}</span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
